import { useMemo, useRef, useState } from "react";
import { usePathAgents } from "./PathAgentsContext";
import {
    clusterConsecutiveBySpawnGroupId,
    TEXT_BRUSH_FONT_FAMILY,
} from "../../utils/pathAgents";
import { TrailPaths } from "./AgentTrailPaths";
import { clientToSvgUserSpace } from "../../utils/svgViewBoxPointer";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";
import type {
    AgentStamp,
    CompletedAgentPath,
    PathAgent,
} from "./PathAgentsContext";

function StampMarks({
    stamps,
    strokeColor,
    opacity,
    stampKeyPrefix,
}: {
    stamps: AgentStamp[];
    strokeColor: string;
    opacity?: number;
    stampKeyPrefix: string;
}) {
    if (stamps.length === 0) return null;
    return (
        <g className="path-agents-stamps" opacity={opacity}>
            {stamps.map((s, i) =>
                s.kind === "circle" ? (
                    <circle
                        key={`${stampKeyPrefix}-c-${i}`}
                        cx={s.cx}
                        cy={s.cy}
                        r={s.r}
                        fill="none"
                        stroke={strokeColor}
                        strokeWidth={1.25}
                        strokeLinecap="round"
                    />
                ) : s.kind === "polygon" ? (
                    <polygon
                        key={`${stampKeyPrefix}-p-${i}`}
                        points={s.points.map((p) => `${p.x},${p.y}`).join(" ")}
                        fill="none"
                        stroke={strokeColor}
                        strokeWidth={1.25}
                        strokeLinejoin="round"
                    />
                ) : (
                    <text
                        key={`${stampKeyPrefix}-t-${i}`}
                        x={0}
                        y={0}
                        transform={`translate(${s.x},${s.y}) rotate(${s.rotationDeg})`}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        alignmentBaseline="middle"
                        fill={strokeColor}
                        fontSize={s.fontSize}
                        style={{
                            fontFamily: TEXT_BRUSH_FONT_FAMILY,
                        }}
                    >
                        {s.char}
                    </text>
                ),
            )}
        </g>
    );
}

export function PathAgentsViewer() {
    const {
        viewBoxAttr,
        width,
        height,
        activeAgents,
        completedPaths,
        strokeColor,
        strokeWidth,
        groupSize,
        groupRadius,
        backgroundColor,
        smoothPaths,
        mergeTrailToSinglePath,
        filledVariableWidthStroke,
        trailTaperStartLength,
        trailTaperEndLength,
        strokeDashArray,
        brushTextStrokeUnderlayColor,
        brushTextStrokeUnderlayScale,
        addAgentAt,
        canExport,
        handleDownload,
        inputMode,
        setPuppetBrushTarget,
        clearPuppetBrush,
    } = usePathAgents();

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(" ").map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    const [brushHover, setBrushHover] = useState<{
        x: number;
        y: number;
    } | null>(null);

    const dragRef = useRef<{
        x0: number;
        y0: number;
        x1: number;
        y1: number;
        pointerId: number;
        paintSpawnGroupId?: string;
    } | null>(null);
    const [dragPreview, setDragPreview] = useState<{
        x0: number;
        y0: number;
        x1: number;
        y1: number;
    } | null>(null);

    const SPAWN_DRAG_DIR_THRESHOLD = 4;

    const completedClusters = useMemo(
        () => clusterConsecutiveBySpawnGroupId(completedPaths),
        [completedPaths],
    );
    const activeClusters = useMemo(
        () => clusterConsecutiveBySpawnGroupId(activeAgents),
        [activeAgents],
    );

    const handleSvgPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
        if (event.button !== 0) return;
        const pt = clientToSvgUserSpace(
            event.currentTarget,
            event.clientX,
            event.clientY,
            viewBox,
        );
        if (!pt) return;
        event.currentTarget.setPointerCapture(event.pointerId);

        if (inputMode === "dragPaint") {
            const gid = addAgentAt(pt.x, pt.y, null, {
                anchorLeaderAtPointer: true,
            });
            dragRef.current = {
                x0: pt.x,
                y0: pt.y,
                x1: pt.x,
                y1: pt.y,
                pointerId: event.pointerId,
                paintSpawnGroupId: gid,
            };
            if (gid) {
                setPuppetBrushTarget(gid, pt.x, pt.y);
            }
            setDragPreview(null);
            setBrushHover(null);
            return;
        }

        dragRef.current = {
            x0: pt.x,
            y0: pt.y,
            x1: pt.x,
            y1: pt.y,
            pointerId: event.pointerId,
        };
        setDragPreview({
            x0: pt.x,
            y0: pt.y,
            x1: pt.x,
            y1: pt.y,
        });
        setBrushHover(null);
    };

    const handleSvgPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
        const pt = clientToSvgUserSpace(
            event.currentTarget,
            event.clientX,
            event.clientY,
            viewBox,
        );
        if (!pt) return;
        const d = dragRef.current;
        if (d && d.pointerId === event.pointerId) {
            d.x1 = pt.x;
            d.y1 = pt.y;
            if (d.paintSpawnGroupId) {
                setPuppetBrushTarget(d.paintSpawnGroupId, pt.x, pt.y);
                if (groupSize > 1 && groupRadius > 0) {
                    setBrushHover({ x: pt.x, y: pt.y });
                }
            } else {
                setDragPreview({
                    x0: d.x0,
                    y0: d.y0,
                    x1: d.x1,
                    y1: d.y1,
                });
            }
        } else {
            setBrushHover(pt);
        }
    };

    const finishPointerInteraction = (
        target: SVGSVGElement,
        pointerId: number,
    ) => {
        const d = dragRef.current;
        if (!d || d.pointerId !== pointerId) return;
        dragRef.current = null;
        setDragPreview(null);
        try {
            target.releasePointerCapture(pointerId);
        } catch {
            /* already released */
        }
        if (d.paintSpawnGroupId) {
            clearPuppetBrush();
            return;
        }
        const dx = d.x1 - d.x0;
        const dy = d.y1 - d.y0;
        if (Math.hypot(dx, dy) < SPAWN_DRAG_DIR_THRESHOLD) {
            addAgentAt(d.x0, d.y0);
        } else {
            addAgentAt(d.x0, d.y0, { dx, dy });
        }
    };

    const handleSvgPointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
        if (event.button !== 0) return;
        finishPointerInteraction(event.currentTarget, event.pointerId);
    };

    const handleSvgPointerCancel = (
        event: React.PointerEvent<SVGSVGElement>,
    ) => {
        const d = dragRef.current;
        if (!d || d.pointerId !== event.pointerId) return;
        dragRef.current = null;
        setDragPreview(null);
        if (d.paintSpawnGroupId) {
            clearPuppetBrush();
        }
        try {
            event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
            /* already released */
        }
    };

    const handleSvgPointerLeave = () => {
        if (dragRef.current) return;
        setBrushHover(null);
    };

    return (
        <div className="panel-viewer blob-field-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>

                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill">
                        <svg
                            ref={svgRef}
                            viewBox={viewBox}
                            xmlns="http://www.w3.org/2000/svg"
                            className="path-tool-svg"
                            onPointerDown={(e) => {
                                panHandlers.onPointerDown(e);
                                handleSvgPointerDown(e);
                            }}
                            onPointerMove={(e) => {
                                panHandlers.onPointerMove(e);
                                handleSvgPointerMove(e);
                            }}
                            onPointerUp={(e) => {
                                panHandlers.onPointerUp(e);
                                handleSvgPointerUp(e);
                            }}
                            onPointerCancel={(e) => {
                                panHandlers.onPointerCancel(e);
                                handleSvgPointerCancel(e);
                            }}
                            onPointerLeave={handleSvgPointerLeave}
                            style={{ cursor: "crosshair", touchAction: "none" }}
                        >
                            <rect
                                width={width}
                                height={height}
                                fill={backgroundColor}
                            />
                            <g className="path-agents-trails">
                                {completedClusters.map((cluster, ci) => (
                                    <g
                                        key={`done-${cluster.spawnGroupId}-${ci}`}
                                        className="path-agents-spawn-group"
                                        data-spawn-group={cluster.spawnGroupId}
                                    >
                                        {cluster.items.map(
                                            (p: CompletedAgentPath) => {
                                                const mode =
                                                    p.brushMode ?? "stroke";
                                                const isTrailMode =
                                                    mode === "stroke" ||
                                                    mode === "textWithStroke";
                                                const trail = isTrailMode ? (
                                                    <TrailPaths
                                                        key={`${p.id}-trail`}
                                                        points={p.points}
                                                        strokeWidths={
                                                            p.strokeWidths?.map(
                                                                (w) =>
                                                                    w *
                                                                    (mode ===
                                                                    "textWithStroke"
                                                                        ? Math.max(
                                                                              1,
                                                                              brushTextStrokeUnderlayScale,
                                                                          )
                                                                        : 1),
                                                            )
                                                        }
                                                        smoothPaths={
                                                            smoothPaths
                                                        }
                                                        strokeColor={
                                                            mode ===
                                                            "textWithStroke"
                                                                ? brushTextStrokeUnderlayColor
                                                                : (p.strokeColor ??
                                                                  strokeColor)
                                                        }
                                                        fallbackWidth={
                                                            strokeWidth *
                                                            (mode ===
                                                            "textWithStroke"
                                                                ? Math.max(
                                                                      1,
                                                                      brushTextStrokeUnderlayScale,
                                                                  )
                                                                : 1)
                                                        }
                                                        mergeVaryingWidthToSinglePath={
                                                            mergeTrailToSinglePath
                                                        }
                                                        filledVariableWidthStroke={
                                                            filledVariableWidthStroke
                                                        }
                                                        strokeDashArray={
                                                            strokeDashArray
                                                        }
                                                        trailTaperStartLength={
                                                            trailTaperStartLength
                                                        }
                                                        trailTaperEndLength={
                                                            trailTaperEndLength
                                                        }
                                                    />
                                                ) : null;
                                                const stamps =
                                                    mode === "text" ||
                                                    mode === "textWithStroke" ? (
                                                        <StampMarks
                                                            key={`${p.id}-stamps`}
                                                            stampKeyPrefix={`done-${p.id}`}
                                                            stamps={p.stamps ?? []}
                                                            strokeColor={
                                                                p.strokeColor ??
                                                                strokeColor
                                                            }
                                                        />
                                                    ) : mode !== "stroke" ? (
                                                        <StampMarks
                                                            key={`${p.id}-stamps`}
                                                            stampKeyPrefix={`done-${p.id}`}
                                                            stamps={p.stamps ?? []}
                                                            strokeColor={
                                                                p.strokeColor ??
                                                                strokeColor
                                                            }
                                                        />
                                                    ) : null;
                                                return (
                                                    <g key={p.id}>
                                                        {trail}
                                                        {stamps}
                                                    </g>
                                                );
                                            },
                                        )}
                                    </g>
                                ))}
                                {activeClusters.map((cluster, ci) => (
                                    <g
                                        key={`act-${cluster.spawnGroupId}-${ci}`}
                                        className="path-agents-spawn-group"
                                        data-spawn-group={cluster.spawnGroupId}
                                    >
                                        {cluster.items.map((a: PathAgent) =>
                                            (() => {
                                                const mode = a.brushMode;
                                                const isTrailMode =
                                                    mode === "stroke" ||
                                                    mode === "textWithStroke";
                                                return (
                                                    <g key={a.id}>
                                                        {isTrailMode && (
                                                            <TrailPaths
                                                                points={a.points}
                                                                strokeWidths={a.strokeWidths.map(
                                                                    (w) =>
                                                                        w *
                                                                        (mode ===
                                                                        "textWithStroke"
                                                                            ? Math.max(
                                                                                  1,
                                                                                  brushTextStrokeUnderlayScale,
                                                                              )
                                                                            : 1),
                                                                )}
                                                                smoothPaths={smoothPaths}
                                                                strokeColor={
                                                                    mode ===
                                                                    "textWithStroke"
                                                                        ? brushTextStrokeUnderlayColor
                                                                        : (a.strokeColor ??
                                                                          strokeColor)
                                                                }
                                                                fallbackWidth={
                                                                    strokeWidth *
                                                                    (mode ===
                                                                    "textWithStroke"
                                                                        ? Math.max(
                                                                              1,
                                                                              brushTextStrokeUnderlayScale,
                                                                          )
                                                                        : 1)
                                                                }
                                                                mergeVaryingWidthToSinglePath={
                                                                    mergeTrailToSinglePath
                                                                }
                                                                filledVariableWidthStroke={
                                                                    filledVariableWidthStroke
                                                                }
                                                                strokeDashArray={
                                                                    strokeDashArray
                                                                }
                                                                trailTaperStartLength={
                                                                    trailTaperStartLength
                                                                }
                                                                trailTaperEndLength={
                                                                    trailTaperEndLength
                                                                }
                                                                opacity={0.95}
                                                            />
                                                        )}
                                                        {(mode !== "stroke") && (
                                                            <StampMarks
                                                                stampKeyPrefix={a.id}
                                                                stamps={a.stamps}
                                                                strokeColor={
                                                                    a.strokeColor ??
                                                                    strokeColor
                                                                }
                                                                opacity={0.95}
                                                            />
                                                        )}
                                                    </g>
                                                );
                                            })(),
                                        )}
                                    </g>
                                ))}
                            </g>
                            {(brushHover || dragPreview) &&
                                groupSize > 1 &&
                                groupRadius > 0 && (
                                    <g
                                        className="path-agents-brush-hover"
                                        aria-hidden
                                    >
                                        <circle
                                            className="svg-brush-hover-preview"
                                            cx={
                                                dragPreview
                                                    ? dragPreview.x0
                                                    : brushHover!.x
                                            }
                                            cy={
                                                dragPreview
                                                    ? dragPreview.y0
                                                    : brushHover!.y
                                            }
                                            r={groupRadius}
                                            stroke={strokeColor}
                                        />
                                    </g>
                                )}
                            {dragPreview &&
                                inputMode === "clickSpawn" && (
                                    <g
                                        className="path-agents-spawn-direction"
                                        aria-hidden
                                        pointerEvents="none"
                                    >
                                        <line
                                            className="flow-agents-spawn-direction-line"
                                            x1={dragPreview.x0}
                                            y1={dragPreview.y0}
                                            x2={dragPreview.x1}
                                            y2={dragPreview.y1}
                                            stroke={strokeColor}
                                        />
                                    </g>
                                )}
                            <g className="path-agents-heads">
                                {activeAgents.map((a) => {
                                    const r = Math.max(
                                        2,
                                        a.liveStrokeWidth * 1.2,
                                    );
                                    const isGroupLeader =
                                        a.isGroupLeader === true;
                                    const isFollower = Boolean(a.leaderId);
                                    return (
                                        <circle
                                            key={`${a.id}-head`}
                                            cx={a.x}
                                            cy={a.y}
                                            r={r}
                                            fill={
                                                a.strokeColor ?? strokeColor
                                            }
                                            stroke={
                                                isGroupLeader
                                                    ? "#e8b832"
                                                    : isFollower
                                                      ? "rgba(255,255,255,0.4)"
                                                      : "none"
                                            }
                                            strokeWidth={
                                                isGroupLeader
                                                    ? 2
                                                    : isFollower
                                                      ? 1
                                                      : 0
                                            }
                                        />
                                    );
                                })}
                            </g>
                        </svg>
                    </div>
                </div>

                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {completedPaths.length} finished path
                        {completedPaths.length !== 1 ? "s" : ""} -{" "}
                        {activeAgents.length} active agent
                        {activeAgents.length !== 1 ? "s" : ""} - viewBox{" "}
                        {viewBoxAttr}
                    </p>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download result SVG
                    </button>
                </div>
            </div>
        </div>
    );
}
